import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { AuthContext } from './auth';
import { tokenService } from '../services/tokenService';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const refreshTimerRef = useRef(null);
  const isRefreshingRef = useRef(false);

  // Schedule automatic token refresh
  const scheduleTokenRefresh = useCallback(() => {
    // Clear any existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    const timeUntilExpiry = tokenService.getTimeUntilExpiry();
    if (!timeUntilExpiry || timeUntilExpiry <= 0) {
      return;
    }

    // Schedule refresh 5 minutes before expiry
    const refreshTime = Math.max(0, timeUntilExpiry - 5 * 60 * 1000);
    
    refreshTimerRef.current = setTimeout(async () => {
      await refreshToken();
    }, refreshTime);
  }, []);

  // Refresh access token using refresh token
  const refreshToken = useCallback(async () => {
    // Prevent multiple simultaneous refresh attempts
    if (isRefreshingRef.current) {
      return;
    }

    const refreshTokenValue = tokenService.getRefreshToken();
    if (!refreshTokenValue) {
      logout();
      return;
    }

    isRefreshingRef.current = true;

    try {
      const response = await api.post(API_ENDPOINTS.AUTH.REFRESH, {
        refresh_token: refreshTokenValue
      });

      const { access_token, refresh_token, token_type, expires_at } = response.data;
      
      // Update tokens
      tokenService.setTokens(access_token, refresh_token, token_type, expires_at);
      
      // Schedule next refresh
      scheduleTokenRefresh();
      
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
      return false;
    } finally {
      isRefreshingRef.current = false;
    }
  }, [scheduleTokenRefresh]);

  // Check session validity
  const checkSession = useCallback(async () => {
    try {
      const response = await api.get(API_ENDPOINTS.AUTH.SESSION);
      return response.data.is_valid;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const token = tokenService.getAccessToken();
      
      if (token) {
        try {
          // Check if token is expired or expiring soon
          if (tokenService.isTokenExpired()) {
            // Try to refresh the token
            const refreshSuccess = await refreshToken();
            if (!refreshSuccess) {
              setLoading(false);
              return;
            }
          } else if (tokenService.isTokenExpiringSoon()) {
            // Schedule refresh if expiring soon
            refreshToken();
          } else {
            // Schedule future refresh
            scheduleTokenRefresh();
          }

          // Get user info
          const response = await api.get(API_ENDPOINTS.AUTH.ME);
          setUser(response.data);
          setIsAuthenticated(true);
        } catch {
          logout();
        }
      }
      setLoading(false);
    };

    initAuth();

    // Handle visibility change - check session when tab becomes visible
    const handleVisibilityChange = () => {
      if (!document.hidden && tokenService.getAccessToken()) {
        if (tokenService.isTokenExpiringSoon()) {
          refreshToken();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [refreshToken, scheduleTokenRefresh]);

  const login = async (credentials) => {
    const formData = new FormData();
    formData.append('username', credentials.username);
    formData.append('password', credentials.password);

    const response = await api.post(API_ENDPOINTS.AUTH.LOGIN, formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });

    const { access_token, refresh_token, token_type, expires_at } = response.data;
    
    // Store all token data
    tokenService.setTokens(access_token, refresh_token, token_type, expires_at);
    
    // Schedule token refresh
    scheduleTokenRefresh();
    
    const userResponse = await api.get(API_ENDPOINTS.AUTH.ME);
    setUser(userResponse.data);
    setIsAuthenticated(true);
    
    return response.data;
  };

  const register = async (userData) => {
    const response = await api.post(API_ENDPOINTS.AUTH.REGISTER, userData);
    return response.data;
  };

  const logout = useCallback(() => {
    // Clear refresh timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    // Clear all tokens
    tokenService.clearAll();
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  const updateProfile = useCallback(async () => {
    // Refresh user data after profile update
    try {
      const response = await api.get(API_ENDPOINTS.AUTH.ME);
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      return null;
    }
  }, []);

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    register,
    logout,
    refreshToken,
    checkSession,
    updateProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
